import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

export const SETTINGS_BASE = "/settings"

/** URL segment of every settings page, under `/settings`. */
export const SETTINGS_SEGMENTS = {
  setup: "setup",
  profile: "profile",
  notifications: "notifications",
  signatures: "signatures",
  usage: "usage",
  general: "general",
  emailSending: "email-sending",
  members: "members",
  regulations: "regulations",
  masterData: "master-data",
  conformityDeclarations: "conformity-declarations",
  supplierInquiries: "supplier-inquiries",
} as const

export type SettingsSectionId = keyof typeof SETTINGS_SEGMENTS

/** Every page's title: the nav's, the breadcrumb's, and what a link to the
 *  page from elsewhere is labelled, so both say the same. */
export const SETTINGS_TITLE_KEYS: Record<SettingsSectionId, TranslationKeys> = {
  setup: "SETTINGS/SETUP/TITLE",
  profile: "SETTINGS/PROFILE/TITLE",
  notifications: "SETTINGS/NOTIFICATIONS/TITLE",
  signatures: "SETTINGS/SIGNATURES/TITLE",
  usage: "SETTINGS/USAGE/TITLE",
  general: "SETTINGS/GENERAL/TITLE",
  emailSending: "SETTINGS/EMAIL_SENDING/TITLE",
  members: "SETTINGS/MEMBERS/TITLE",
  regulations: "SETTINGS/REGULATIONS/TITLE",
  masterData: "SETTINGS/MASTER_DATA/TITLE",
  conformityDeclarations: "SETTINGS/DECLARATIONS/TITLE",
  supplierInquiries: "SETTINGS/SUPPLIER_INQUIRIES/TITLE",
}

/** Absolute path of every settings page. Link here, never to `/settings#…`. */
export const SETTINGS_PATHS = Object.fromEntries(
  Object.entries(SETTINGS_SEGMENTS).map(([id, segment]) => [
    id,
    `${SETTINGS_BASE}/${segment}`,
  ]),
) as Record<SettingsSectionId, string>

/**
 * Whether `pathname` is a page of the settings app, with its nav, rather
 * than a full-page editor that also lives under `/settings` (the declaration
 * template editor).
 */
export function isSettingsPagePath(pathname: string): boolean {
  return (
    pathname === SETTINGS_BASE ||
    Object.values(SETTINGS_PATHS).some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    )
  )
}

/** The tabs of the Supplier inquiries page, each its own URL. */
export const SUPPLIER_INQUIRY_TABS = [
  "emails",
  "deadlines",
  "attachment",
] as const
export type SupplierInquiryTab = (typeof SUPPLIER_INQUIRY_TABS)[number]

export const supplierInquiriesTabPath = (tab: SupplierInquiryTab) =>
  `${SETTINGS_PATHS.supplierInquiries}/${tab}`

export const SUPPLIER_INQUIRY_TAB_TITLE_KEYS: Record<
  SupplierInquiryTab,
  TranslationKeys
> = {
  emails: "SETTINGS/SUPPLIER_INQUIRIES/TAB_EMAILS",
  deadlines: "SETTINGS/SUPPLIER_INQUIRIES/TAB_DEADLINES",
  attachment: "SETTINGS/SUPPLIER_INQUIRIES/TAB_ATTACHMENT",
}

/** The full-page declaration template editor, opened from its card. */
export const declarationTemplateEditorPath = (key: string) =>
  `${SETTINGS_BASE}/declaration-templates/${key}`

/** Where `/settings` lands when no page is named, once nothing is left to set
 *  up; until then it opens the setup checklist. */
export const DEFAULT_SETTINGS_SECTION: SettingsSectionId = "profile"

/** Pages that were folded into another one, by their old URL segment. */
export const LEGACY_SETTINGS_SEGMENTS: Record<string, SettingsSectionId> = {
  // Language & preferences, now part of Profile.
  preferences: "profile",
}

/**
 * The single settings page used `#anchors` for its cards. Old links (bookmarks,
 * sent emails) still carry them, so each one maps to the page that card became.
 */
export const LEGACY_SETTINGS_HASHES: Record<string, SettingsSectionId> = {
  notifications: "notifications",
  signatures: "signatures",
  regulations: "regulations",
  "declaration-templates": "conformityDeclarations",
}
